import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const UserContext = createContext();

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  // Tur düzenleyici: admin bayi görünümüne geçebilir
  const [viewAsRole, setViewAsRole] = useState(null);

  // Gerçek admin mi? (viewAsRole ne olursa olsun)
  const isRealAdmin = useMemo(() => {
    return user?.role === 'admin' || user?.rol === 'admin';
  }, [user]);

  // Effective user: viewAsRole aktifken rol override edilir
  const effectiveUser = useMemo(() => {
    if (!user || !viewAsRole) return user;
    return { ...user, role: viewAsRole, rol: viewAsRole };
  }, [user, viewAsRole]);

  // Seçili firma state'i
  const [selectedFirma, setSelectedFirma] = useState(() => {
    const stored = localStorage.getItem("selectedFirma");
    return stored ? JSON.parse(stored) : null;
  });

  // Firma seçim modalı kontrolü
  const [showFirmaSecimModal, setShowFirmaSecimModal] = useState(false);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  // selectedFirma'yı localStorage'a persist et
  useEffect(() => {
    if (selectedFirma) {
      localStorage.setItem("selectedFirma", JSON.stringify(selectedFirma));
    } else {
      localStorage.removeItem("selectedFirma");
    }
  }, [selectedFirma]);

  const login = (userData) => {
    if (userData && !userData.type) {
      userData.type = 'member';
    }
    setUser(userData);

    // Login sonrası firma seçim mantığı
    if (userData?.firmalar && userData.firmalar.length > 0) {
      if (userData.firmalar.length === 1) {
        // Tek firma varsa otomatik seç
        setSelectedFirma(userData.firmalar[0]);
      } else {
        // Birden fazla firma varsa: önce localStorage'da kayıtlı firma var mı kontrol et
        const storedFirma = localStorage.getItem("selectedFirma");
        if (storedFirma) {
          const parsed = JSON.parse(storedFirma);
          const found = userData.firmalar.find(f => f.id === parsed.id);
          if (found) {
            setSelectedFirma(found);
          } else {
            // Kayıtlı firma artık yetkilerde yoksa, default firma seç veya modal göster
            const defaultFirma = userData.firmalar.find(f => f.is_default);
            if (defaultFirma) {
              setSelectedFirma(defaultFirma);
            } else {
              setShowFirmaSecimModal(true);
            }
          }
        } else {
          // localStorage'da yoksa default firmayı seç veya seçim modalı göster
          const defaultFirma = userData.firmalar.find(f => f.is_default);
          if (defaultFirma) {
            setSelectedFirma(defaultFirma);
          } else {
            setShowFirmaSecimModal(true);
          }
        }
      }
    } else {
      setSelectedFirma(null);
    }
  };

  const logout = () => {
    setUser(null);
    setSelectedFirma(null);
    localStorage.removeItem('teklifFirmaInfo');
    localStorage.removeItem('selectedFirma');
  };

  // Firma seçme fonksiyonu
  const selectFirma = useCallback((firma) => {
    setSelectedFirma(firma);
    setShowFirmaSecimModal(false);
  }, []);

  // Firma listesini güncelle (API'den çektikten sonra)
  const updateFirmalar = useCallback((firmalar) => {
    if (user) {
      setUser(prev => ({ ...prev, firmalar }));
    }
  }, [user]);

  return (
    <UserContext.Provider value={{
      user: effectiveUser,
      login,
      logout,
      selectedFirma,
      selectFirma,
      showFirmaSecimModal,
      setShowFirmaSecimModal,
      updateFirmalar,
      viewAsRole,
      setViewAsRole,
      isRealAdmin
    }}>
      {children}
    </UserContext.Provider>
  );
}
